import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isRegister && form.password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      if (isRegister) await register(form.email, form.password, form.name || undefined);
      else await login(form.email, form.password);
      navigate("/account");
      toast.success(isRegister ? "Account created" : "Welcome back");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t.common.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="container max-w-5xl">
        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <section className="panel panel-muted p-8 md:p-10">
            <span className="eyebrow">{isRegister ? t.auth.register : t.auth.login}</span>
            <h1 className="mb-8 text-[3rem] leading-[1.14]">
              {isRegister ? t.auth.createCollectorAccount : t.auth.collectorSignIn}
            </h1>
            <p style={{ color: "var(--color-text-muted)" }}>
              {t.auth.accountDescription}
            </p>
          </section>

          <form onSubmit={handleSubmit} className="panel form-grid p-8 md:p-10">
            <a
              href="/api/auth/google"
              className="btn-secondary w-full"
            >
              {t.auth.loginWithGoogle}
            </a>

            <div className="flex items-center gap-4">
              <div className="h-px flex-1" style={{ backgroundColor: "var(--color-border)" }} />
              <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--color-text-light)" }}>
                Email
              </span>
              <div className="h-px flex-1" style={{ backgroundColor: "var(--color-border)" }} />
            </div>

            {isRegister && (
              <div>
                <label className="form-label">{t.checkout.name}</label>
                <input
                  className="input-shell"
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>
            )}

            <div>
              <label className="form-label">{t.auth.email}</label>
              <input
                className="input-shell"
                type="email"
                required
                value={form.email}
                onChange={(event) => setForm({ ...form, email: event.target.value })}
              />
            </div>

            <div>
              <label className="form-label">{t.auth.password}</label>
              <input
                className="input-shell"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(event) => setForm({ ...form, password: event.target.value })}
              />
            </div>

            {isRegister && (
              <div>
                <label className="form-label">{t.auth.confirmPassword}</label>
                <input
                  className="input-shell"
                  type="password"
                  required
                  minLength={6}
                  value={form.confirmPassword}
                  onChange={(event) => setForm({ ...form, confirmPassword: event.target.value })}
                />
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? t.common.loading : isRegister ? t.auth.createAccount : t.auth.login}
            </button>

            <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>
              {isRegister ? t.auth.hasAccount : t.auth.noAccount}{" "}
              <button
                type="button"
                onClick={() => setIsRegister((value) => !value)}
                className="font-semibold"
                style={{ color: "var(--color-terracotta)" }}
              >
                {isRegister ? t.auth.login : t.auth.register}
              </button>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
